import { cn } from '@apps/web/lib/utils';
import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    BoundedArtifactPayload,
    stripUntrustedMarkers,
    toRecord,
    truncateArtifactText,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

function payloadText(
    payload: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): string | null {
    for (const key of keys) {
        const value = payload[key];
        if (typeof value === 'string') {
            return stripUntrustedMarkers(value);
        }
    }
    return null;
}

function ConsoleArtifactChip({
    label,
    tone = 'neutral',
}: {
    readonly label: string;
    readonly tone?: 'danger' | 'neutral' | 'ok';
}) {
    return (
        <span
            className={cn(
                'rounded-sm px-1.5 py-0.5 font-mono text-[11px] leading-none',
                tone === 'ok' && 'bg-green-50 text-green-700',
                tone === 'danger' && 'bg-red-50 text-red-700',
                tone === 'neutral' && 'bg-muted text-muted-foreground',
            )}
        >
            {label}
        </span>
    );
}

function ConsoleArtifactTextBlock({
    label,
    text,
    tone = 'neutral',
}: {
    readonly label: string | null;
    readonly text: string;
    readonly tone?: 'danger' | 'neutral';
}) {
    const { text: visibleText, omittedChars } = truncateArtifactText(text);
    return (
        <div>
            {label ? (
                <p className="text-muted-foreground mb-1 font-mono text-[10px] font-medium tracking-wide uppercase">
                    {label}
                </p>
            ) : null}
            <pre
                className={cn(
                    'rounded-md px-3 py-2 font-mono text-[12px] leading-5 whitespace-pre-wrap',
                    tone === 'danger'
                        ? 'bg-red-50 text-red-700'
                        : 'bg-muted/40 text-foreground',
                )}
            >
                {visibleText}
            </pre>
            {omittedChars > 0 ? (
                <p className="text-muted-foreground mt-1 text-[11px]">
                    Truncated — {omittedChars.toLocaleString()} more characters
                    in the raw payload below.
                </p>
            ) : null}
        </div>
    );
}

/**
 * Renderer for process-output artifacts (sandbox
 * commands): monospace stdout/stderr with outcome chips. Only real
 * payload fields render — an absent exit code is omitted, never
 * invented — and text/raw rendering is size-capped.
 */
export function ConsoleOutputArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const payload = toRecord(artifact.payload);
    const stdout = payloadText(payload, ['stdout', 'output', 'content']) ?? '';
    const stderr = payloadText(payload, ['stderr']) ?? '';
    const exitCode =
        typeof payload['exit_code'] === 'number' ? payload['exit_code'] : null;
    const durationMs =
        typeof payload['duration_ms'] === 'number'
            ? payload['duration_ms']
            : null;
    const timedOut = payload['timed_out'] === true;
    const hasStdout = stdout.trim().length > 0;
    const hasStderr = stderr.trim().length > 0;
    const hasChips = exitCode !== null || durationMs !== null || timedOut;
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            {hasChips ? (
                <div className="flex flex-wrap items-center gap-1.5">
                    {exitCode !== null ? (
                        <ConsoleArtifactChip
                            label={`exit ${exitCode}`}
                            tone={exitCode === 0 ? 'ok' : 'danger'}
                        />
                    ) : null}
                    {timedOut ? (
                        <ConsoleArtifactChip label="timed out" tone="danger" />
                    ) : null}
                    {durationMs !== null ? (
                        <ConsoleArtifactChip
                            label={
                                durationMs < 1_000
                                    ? `${durationMs} ms`
                                    : `${(durationMs / 1_000).toFixed(1)} s`
                            }
                        />
                    ) : null}
                </div>
            ) : null}
            {hasStdout ? (
                <ConsoleArtifactTextBlock
                    label={hasStderr ? 'stdout' : null}
                    text={stdout}
                />
            ) : null}
            {hasStderr ? (
                <ConsoleArtifactTextBlock
                    label="stderr"
                    text={stderr}
                    tone="danger"
                />
            ) : null}
            {!hasStdout && !hasStderr ? (
                <p className="text-muted-foreground text-xs">No output.</p>
            ) : null}
            <ArtifactEvidence artifact={artifact} />
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <BoundedArtifactPayload payload={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}
